'use client';

import { PropsWithChildren } from "react";

interface ModalProps {
    open: boolean;
    onclose?(): void;
    onconfirm?(): void;
    buttonText?: string;
    id?: string;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | `[${number}px]`,
    buttonClass?: string;
}

import { Fragment, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";

const ModalDialog = ({ open, onclose, onconfirm, children, buttonText, maxWidth, id, buttonClass }: PropsWithChildren<ModalProps>) => {

    const cancelButtonRef = useRef(null);
    const transitionRef = useRef(null);

    return (
        <Transition.Root show={open} as={Fragment} ref={transitionRef}>
            <Dialog id={id} as="div" className="relative z-20" initialFocus={cancelButtonRef} onClose={onclose ? onclose : () => undefined}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-20 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8 text-center ">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-100"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-100"
                        >
                            <Dialog.Panel className={clsx("relative transform overflow-hidden rounded-xl bg-white dark:bg-gray-900 px-4 pb-4 pt-5 text-left shadow-xl transition-all w-fit", maxWidth ? `max-w-${maxWidth}` : 'max-w-lg')}>
                                {onclose && <button onClick={onclose} ref={cancelButtonRef} className="absolute top-3 right-4 z-20 text-gray-400 rounded-full p-2 hover:bg-gray-200 hover:shadow">
                                    <XMarkIcon className="w-6 h-6" />
                                </button>}
                                {children}
                                {onconfirm && <div className="w-full mt-5 sm:mt-6">
                                    <button
                                        type="button"
                                        className={clsx(
                                            "w-full font-semibold py-2 px-4 ring-1 ring-gray-300 text-gray-900 rounded flex justify-center items-center",
                                            " hover:shadow hover:ring-gray-500",
                                            "dark:bg-gray-900 dark:hover:bg-secondary-main dark:text-white dark:border-white dark:hover:text-secondary-contrastText dark:hover:border-secondary-contrastText",
                                            buttonClass
                                        )}
                                        onClick={onconfirm}
                                    >
                                        {buttonText ? buttonText : 'Confirm'}
                                    </button>
                                </div>}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
export default ModalDialog;